/** @file Creates consumer potentials from the shared masterdata variant picker. */
import {useRef, useState} from "react";
import {usePotentialCreateMutation} from "../../../../Queries";
import {MasterDataSearchDialog} from "../../../brand/MasterDataSearchDialog";

type PotentialAddDialogProps = {
   brandHouseId: string
   consumerId: string
   onCancel: () => void
   onCreated: () => void
}

/** Adds the selected masterdata variant using Core's price and date defaults. */
export const PotentialAddDialog = ({brandHouseId, consumerId, onCancel, onCreated}: PotentialAddDialogProps) => {
   const [createPotential, {loading}] = usePotentialCreateMutation();
   const [errorMessage, setErrorMessage] = useState<string>();
   const submitting = useRef(false);

   /** Prevents duplicate submissions while Core creates the selected potential. */
   const onPickVariant = async (masterVariantId: string) => {
      if (submitting.current) {
         return;
      }
      submitting.current = true;
      setErrorMessage(undefined);
      try {
         const {data} = await createPotential({
            variables: {consumerId, masterVariantId},
            // Core creates a new record on each call, so a lost response must not replay it.
            context: {skipRetry: true}
         });
         if (!data?.PotentialCreate?._id) {
            throw new Error("Unable to create potential. Please try again.");
         }
      } catch (error) {
         setErrorMessage(error instanceof Error ? error.message : "Unable to create potential. Please try again.");
         submitting.current = false;
         return;
      }
      onCreated();
   };

   /** Keeps the dialog open until an in-flight creation has finished. */
   const onClose = () => {
      if (!submitting.current) {
         onCancel();
      }
   };

   return <MasterDataSearchDialog
      brandHouseId={brandHouseId}
      onPickVariant={onPickVariant}
      onCancel={onClose}
      busy={loading}
      errorMessage={errorMessage}
   />;
};
